import { useCallback, useMemo, useRef, useState, RefObject } from 'react';
import {
  find,
  findIndex,
  map,
  forEach,
  compact,
  first,
  isEmpty,
  get,
} from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { getCOSPresigned } from '../../service/service';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import useExcelWorker, { IExcelAllSheetsResult } from './useExcelWorker';
import type { IExcelParseResult } from '../utils';
import { padSheetDataToMinSize } from '../utils';
import type { ISheet } from '../components/ExcelSheetTabs';
import type { ISmartTableGridRef } from '../components/SmartTableGrid';
import type { ISheetData } from './useSheetData';
import { useCaseInsensitiveParams, useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';

/**
 * 文件类型：latest 获取最新文件，original 获取原始文件
 */
type TFileType = 'latest' | 'original';

/**
 * 获取 Excel 文件信息的请求参数
 */
interface IFetchExcelParams {
  fileId: string;
  sessionId: string;
  /** 文件类型：latest 获取最新文件（默认），original 获取原始文件（用于重置） */
  type?: TFileType;
  /** 目标文件 ID，用于在返回的全量文件列表中查找对应文件，不传则默认取第一个 */
  targetFileId?: string;
  /** COS 文件链接：get_excel_info 返回空时降级使用，直接读取该链接解析预览 */
  cosUrl?: string;
  /** 是否来自正文 <cos> 标签：true 时跳过 get_excel_info（无 file_id 登记，调用必然返回空），直接读取 cosUrl */
  isCosTag?: boolean;
}

/**
 * Hook 配置选项
 */
interface IUseGetExcelInfoOptions {
  /** 设置单个工作表数据的方法 */
  setSheetData: (sheetId: string, data: ISheetData) => void;
  /** 设置工作表列表的方法 */
  setSheets: React.Dispatch<React.SetStateAction<ISheet[]>>;
  /** 设置当前活动工作表 ID 的方法 */
  setActiveSheetId: React.Dispatch<React.SetStateAction<string>>;
  /** 当前活动工作表 ID */
  activeSheetId: string;
  /** 表格组件的 ref，用于更新表格状态 */
  gridRef?: RefObject<ISmartTableGridRef>;
  /** Worker 超时时间（毫秒），默认 60 秒 */
  workerTimeout?: number;
}

/**
 * 获取并解析 Excel 文件的 Hook
 * 封装了获取文件信息、获取预签名URL、下载Excel、解析Excel的完整流程
 * 支持多 Sheet 解析，内部自动处理数据更新逻辑，调用方无需手动处理回调
 *
 * @param options - 配置选项
 * @returns 请求相关的数据和方法
 */
export const useGetExcelInfo = (options: IUseGetExcelInfoOptions) => {
  const {
    setSheetData,
    setSheets,
    setActiveSheetId,
    activeSheetId,
    gridRef,
    workerTimeout = 60000,
  } = options;

  const showErrorTips = useErrorTips();
  // 支持TempPool页面。后门链接访问，不区分大小写
  const { poolId, msgid } = useCaseInsensitiveParams(useSearchParamsAppoint(), ['poolId', 'msgid']);
  // Excel 解析中状态（从下载到解析完成的整个过程）
  const [isParsing, setIsParsing] = useState<boolean>(false);
  // 当前文件名（用于保存时上传）
  const [currentFileName, setCurrentFileName] = useState<string>('');
  // 当前文件的 COS 路径（后端返回，用于保存时上传到正确路径）
  const [currentFileCosUrl, setCurrentFileCosUrl] = useState<string>('');
  // 缓存所有文件的签名 URL 映射（fileId -> signedUrl），切换文件时直接使用
  // 使用 useRef 而非 useState，因为它仅作为缓存，不需要触发重新渲染
  const signedUrlMapRef = useRef<Record<string, string>>({});

  // Excel 解析
  const [parseExcel, parseStatus, cancelParse] = useExcelWorker({
    timeout: workerTimeout,
    onError: (error) => {
      console.error('Excel 解析失败:', error);
      setIsParsing(false);
    },
  });

  // 原始请求 hook
  const requestData = useRequestController({
    method: 'POST',
    url: `/api/agent/get_excel_info`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
  });

  const { runAsync: originRunAsync, loading, refresh } = requestData;

  /**
   * 处理 Excel 解析成功后的数据更新
   * @param parseResult - 解析结果
   * @param fileName - 文件名
   */
  const handleParseSuccess = useCallback(
    (parseResult: IExcelAllSheetsResult, fileName: string) => {
      const { sheets: parsedSheets, sheetNames } = parseResult;

      // 检查解析结果是否为空
      if (isEmpty(parsedSheets)) {
        console.warn('解析结果为空');
        return false;
      }

      // 获取第一个工作表的数据
      const firstSheet = first(parsedSheets)!;
      const { tableData, columns, isTruncated, originalRowCount } = firstSheet;

      // 如果数据被截取，打印提示信息
      if (isTruncated) {
        console.warn(
          `数据已被截取，原始行数: ${originalRowCount}，当前行数: ${tableData.length}`,
        );
      }

      if (isEmpty(tableData) || isEmpty(columns)) {
        console.warn('数据为空，columns:', columns, 'tableData:', tableData);
        return false;
      }

      // 如果有多个工作表，更新 sheets 状态
      if (parsedSheets.length > 1) {
        // 生成新的工作表配置
        const newSheets: ISheet[] = map(
          parsedSheets,
          (sheet: IExcelParseResult, index: number) => ({
            id: String(index + 1),
            name:
              sheetNames?.[index] || sheet.sheetName || `工作表${index + 1}`,
          }),
        );
        setSheets(newSheets);
        setActiveSheetId('1');

        // 批量更新所有工作表的数据（填充到至少默认空白尺寸）
        forEach(parsedSheets, (sheet: IExcelParseResult, index: number) => {
          const paddedData = padSheetDataToMinSize({
            tableData: sheet.tableData,
            columns: sheet.columns,
          });
          setSheetData(String(index + 1), paddedData);
        });
      } else {
        // 单工作表情况：更新当前 sheet 的数据（填充到至少默认空白尺寸）
        const paddedSingleSheet = padSheetDataToMinSize({ tableData, columns });
        setSheetData(activeSheetId, paddedSingleSheet);

        // 优先使用 Excel 中保存的 sheetName（用户可能已重命名），
        // 只有当解析出的 sheetName 无效时才回退到文件名（去除扩展名）
        const parsedSheetName = firstSheet?.sheetName;
        const fallbackName = fileName
          ? fileName.replace(/\.(xlsx|xls|csv)$/i, '')
          : '';
        const resolvedName = parsedSheetName || fallbackName;

        if (resolvedName) {
          setSheets((prevSheets) =>
            map(prevSheets, (sheet) =>
              sheet.id === activeSheetId
                ? { ...sheet, name: resolvedName }
                : sheet,
            ),
          );
        }
      }

      // 同时更新表格组件的状态（显示第一个工作表，使用填充后的数据）
      const displayData = padSheetDataToMinSize({ tableData, columns });
      gridRef?.current?.setTableState(displayData.tableData, displayData.columns);

      return true;
    },
    [activeSheetId, setSheetData, setSheets, setActiveSheetId, gridRef],
  );

  /**
   * 下载并解析 Excel 文件（公共方法）
   * @param downloadUrl - 预签名的下载 URL
   * @param fileName - 文件名
   * @returns 解析结果
   */
  const downloadAndParseExcel = useCallback(
    async (downloadUrl: string, fileName: string) => {
      // 使用预签名 URL 下载 Excel 文件
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status}`);
      }

      // 获取 ArrayBuffer 并解析 Excel（所有工作表）
      const arrayBuffer = await response.arrayBuffer();

      const parseResult = (await parseExcel(
        arrayBuffer,
      )) as IExcelAllSheetsResult;

      // 内部处理解析结果，更新状态
      if (parseResult) {
        handleParseSuccess(parseResult, fileName);
      }

      return {
        ...parseResult,
        fileName,
      };
    },
    [parseExcel, handleParseSuccess],
  );

  /**
   * 获取并解析 Excel 文件（所有工作表）
   * @param params - 请求参数
   * @param params.fileId - 文件 ID
   * @param params.type - 文件类型：'latest' 获取最新文件（默认），'original' 获取原始文件（用于重置）
   * @returns 所有工作表的解析结果
   */
  const fetchExcelData = useCallback(
    async (params: IFetchExcelParams) => {
      const { sessionId, fileId, type = 'latest', targetFileId, cosUrl, isCosTag } = params;
      const isOriginal = type === 'original';
      setIsParsing(true);

      try {
        // cos 标签来源（正文 <cos> 返回的 xlsx）：无 file_id 登记，跳过 get_excel_info（避免多余请求），
        // 直接落入下方降级分支读取 cos_url 解析预览
        let result: any;
        let files: any[] = [];
        let targetFile: any;

        if (!isCosTag) {
          // 1. 获取文件信息
          result = await originRunAsync({
            trace_id: uuidv4(),
            session_id: sessionId || poolId || msgid,
            file_id: fileId,
          });

          // 根据 targetFileId 在全量文件列表中查找对应文件，找不到则回退到第一个
          files = result?.files || [];
          const matchedFile = targetFileId
            ? find(files, (file: any) => {
              const latestFileId = file?.latest_file?.id;
              const originalFileId = file?.original_file?.id;
              return latestFileId === targetFileId || originalFileId === targetFileId;
            })
            : undefined;
          const firstFile = matchedFile || first(files);

          // 根据 type 获取对应版本的文件信息
          const fileKey = isOriginal ? 'original_file' : 'latest_file';
          targetFile = firstFile?.[fileKey];
        }

        const fileName = targetFile?.name || '';

        // original 模式：只获取单个文件的 COS 路径
        // latest 模式：获取所有文件的 COS 路径
        const fileUrls = isOriginal
          ? compact([targetFile?.cos_url])
          : map(files, (task: any) => task?.latest_file?.cos_url);

        if (isEmpty(fileUrls)) {
          // 降级：cos 标签来源（isCosTag）或 get_excel_info 未返回文件列表时，
          // 若传入 cos_url 则直接读取该链接解析预览
          if (cosUrl) {
            return await fetchSingleFile(fileId, {
              name: cosUrl.split('/').pop() || '',
              cos_url: cosUrl,
            });
          }
          console.warn(isOriginal ? '未找到原始文件信息' : '未找到文件信息');
          setIsParsing(false);
          return null;
        }

        // 2. 批量获取预签名 URL
        const srcList = await getCOSPresigned({
          cos_type: 'agentCos',
          url: fileUrls,
        });

        // 根据目标文件的 cos_url 在签名列表中找到对应的下载 URL
        const targetCosUrl = targetFile?.cos_url;
        const matchedIndex = Math.max(0, findIndex(fileUrls, (url: string) => url === targetCosUrl));
        const downloadUrl = get(srcList, ['url', matchedIndex]) as string | undefined;
        if (!downloadUrl) {
          setIsParsing(false);
          return null;
        }

        // 缓存所有文件的签名 URL 映射（fileId -> signedUrl），方便切换文件时直接使用
        if (!isOriginal && srcList?.url && result?.files) {
          const newSignedUrlMap: Record<string, string> = {};
          forEach(result.files, (file: any, index: number) => {
            const latestFile = file?.latest_file;
            if (latestFile?.id && srcList.url[index]) {
              newSignedUrlMap[latestFile.id] = srcList.url[index];
            }
          });
          signedUrlMapRef.current = newSignedUrlMap;
        }

        // 3. 下载并解析 Excel
        const parseResult = await downloadAndParseExcel(downloadUrl, fileName);

        // 4. 保存当前文件名和 COS 路径（用于保存时上传）
        // 注意：重置操作（original）时不更新文件名和 COS 路径，
        // 因为保存时应始终保存到最新文件的路径上，重置只替换显示数据
        if (!isOriginal) {
          if (fileName) {
            setCurrentFileName(fileName);
          }
          // 保存当前文件的 COS 路径（后端返回的原始路径）
          if (targetFile?.cos_url) {
            setCurrentFileCosUrl(targetFile.cos_url);
          }
        }

        setIsParsing(false);
        return parseResult;
      } catch (error) {
        console.error(
          isOriginal ? '重置失败:' : '下载或解析 Excel 失败:',
          error,
        );
        setIsParsing(false);
        return null;
      }
    },
    [originRunAsync, downloadAndParseExcel],
  );

  /**
   * 获取指定文件的签名下载 URL
   * 优先从 ref 缓存中获取，缓存未命中时重新签名并写入缓存
   * @param fileId - 文件 ID
   * @param cosUrl - 文件的 COS 原始路径
   * @returns 签名后的下载 URL，获取失败返回 null
   */
  const getSignedUrl = useCallback(
    async (fileId: string, cosUrl?: string): Promise<string | null> => {
      // 优先从 ref 缓存中获取（无需依赖 state，ref 始终是最新值）
      const cachedUrl = signedUrlMapRef.current[fileId];
      if (cachedUrl) return cachedUrl;

      // 缓存未命中，重新签名
      if (!cosUrl) return null;

      const srcList = await getCOSPresigned({
        cos_type: 'agentCos',
        url: [cosUrl],
      });
      const signedUrl: string | null = (first(srcList?.url) as string) || null;

      // 写入缓存
      if (signedUrl) {
        const updatedMap: Record<string, string> = {
          ...signedUrlMapRef.current,
          [fileId]: signedUrl,
        };
        signedUrlMapRef.current = updatedMap;
      }

      return signedUrl;
    },
    [], // 无外部依赖，引用永远稳定
  );

  /**
   * 切换文件时，仅下载并解析指定文件，不重新调用接口，保留全量文件列表
   * @param fileId - 要切换到的文件 ID
   * @param file - 文件信息（包含 name、cos_url 等）
   * @returns 解析结果
   */
  const fetchSingleFile = useCallback(
    async (fileId: string, file: { name: string; cos_url?: string; }) => {
      const fileName = file.name || '';
      setIsParsing(true);

      try {
        const downloadUrl = await getSignedUrl(fileId, file.cos_url);

        if (!downloadUrl) {
          console.warn('未找到文件签名 URL，fileId:', fileId);
          setIsParsing(false);
          return null;
        }

        // 下载并解析 Excel
        const parseResult = await downloadAndParseExcel(downloadUrl, fileName);

        // 更新当前文件名和 COS 路径
        if (fileName) {
          setCurrentFileName(fileName);
        }
        if (file.cos_url) {
          setCurrentFileCosUrl(file.cos_url);
        }

        setIsParsing(false);
        return parseResult;
      } catch (error) {
        console.error('切换文件失败:', error);
        setIsParsing(false);
        return null;
      }
    },
    [getSignedUrl, downloadAndParseExcel],
  );

  // 综合的加载状态：API 请求中 或 Excel 解析中
  const isLoading = useMemo(() => {
    return loading || isParsing;
  }, [loading, isParsing]);

  return {
    ...requestData,
    /** 获取并解析 Excel 文件（所有工作表），type='latest' 获取最新，type='original' 获取原始（重置） */
    fetchExcelData,
    /** 是否正在加载（仅 API 请求） */
    loading,
    /** 是否正在解析 Excel（包含下载和解析） */
    isParsing,
    /** 综合加载状态（API 请求中 或 Excel 解析中）- 推荐使用 */
    isLoading,
    /** 解析状态 */
    parseStatus,
    /** 取消解析 */
    cancelParse,
    /** 当前文件名（用于保存时上传） */
    currentFileName,
    /** 当前文件的 COS 路径（后端返回，用于保存时上传到正确路径） */
    currentFileCosUrl,
    /** 刷新请求 */
    refresh,
    /** 切换文件时，仅下载并解析指定文件，不重新调用接口，保留全量文件列表 */
    fetchSingleFile,
  };
};